import {createStore as createVuexStore} from "vuex";
import config from "@/config";
import auth from './auth';
import chat from './chat';
import subscriptions from './subscriptions';
import settings from './settings';
import misc from './misc';
import features from './features';
import operator from './operator';
import videochat from "./videochat";

export const createStore = (app) => {
    return createVuexStore({
        modules: {
            auth,
            videochat,
            settings,
            misc,
            subscriptions: subscriptions(app),
            chat: chat(app),
            features: features(app),
            operator: operator(app),
        },

        state () {
            return {
                translations: {},
                userOnPage: true,
                people: [],
                appLoaded: false,
                isOperator: false,
                isCustomer: false,
            };
        },

        getters: {
            people: (state) => state.people,
            isOperator: (state) => state.isOperator,
            isCustomer: (state) => state.isCustomer,
        },

        mutations: {
            /* GENERAL */
            setDiscoveryPeople(state, data) {
                state.people = data;
            },

            setAppLoaded(state, data) {
                state.appLoaded = data;
            },

            /* CURRENT USER */
            setIsOperator(state, data) {
                state.isOperator = data;
            },

            setIsCustomer(state, data) {
                state.isCustomer = data;
            },
        },

        actions: {
            /**
             * Reload data on page refresh.
             *
             * @param state
             * @param dispatch
             * @param commit
             * @param rootState
             * @returns {Promise<void>}
             */
            async bootstrap({state, dispatch, commit, rootState}) {
                if (!rootState.appLoaded && rootState.auth.authUser) {
                    document.querySelector('#page-loader').style.display = 'block';

                    await dispatch('auth/csrf');

                    if (!state.translations.length) {
                        await dispatch('setupTranslations');
                    }

                    // User role helpers
                    await commit('setIsCustomer', rootState.auth.authUser.role === 'customer');
                    await commit('setIsOperator', rootState.auth.authUser.role === 'operator');

                    // Init settings and features
                    if (!rootState.settings.settingsLoaded) {
                        await dispatch('settings/get');
                    }

                    // Init app data
                    if (!rootState.misc.loaded) {
                        await dispatch('misc/get');
                    }

                    // Customer specific features
                    if (state.isCustomer) {
                        if (!rootState.subscriptions.subscriptionPlans.length) {
                            await dispatch('subscriptions/getSubscriptionPlans');
                        }

                        if (!rootState.features.loaded) {
                            await dispatch('features/load');
                        }
                    }

                    if (!state.chat.loaded) {
                        await dispatch('chat/init');
                    }

                    commit('chat/setReconnecting', true);
                    await dispatch('chat/connect');
                    commit('chat/setReconnecting', false);

                    commit('setAppLoaded', true);

                    document.querySelector('#page-loader').style.display = 'none';

                    window.onblur = () => {
                        state.userOnPage = false;
                    };
                    window.onfocus = () => {
                        state.userOnPage = true;
                    };
                }
            },

            async notify({state, rootState}, {message, passCheck = false}) {
                const notificationsEnabled = rootState.settings['notifications_web'] === 'enabled';
                if (!notificationsEnabled && !passCheck) {
                    return;
                }

                if (!state.userOnPage && !passCheck) {
                    return false;
                }

                if (!('Notification' in window)) {
                    // This browser does not support desktop notification
                } else if (Notification.permission === 'granted') {
                    new Notification(message);
                } else {
                    // Ask the user for permission
                    Notification.requestPermission().then((permission) => {
                        if (permission === 'granted') {
                            new Notification(message);
                        }
                    });
                }
            },

            async fetchCurrentUser({commit, state, dispatch}) {
                await dispatch('auth/fetchCurrentUser');
            },

            async fetchCurrentChatUser({state, dispatch, commit}) {
                const r = await dispatch('fetchUser', state.chat.currentChatUser.id);

                commit('chat/setCurrentChatUser', r.data);
                if (state.isCustomer) {
                    commit('auth/setCurrentUser', r.data);
                } else {
                    commit('chat/updateOperatorUser', r.data);
                }
            },

            async fetchUser({commit}, id) {
                return await window.http.get(`${config.API_URL}/users/${id}`);
            },

            async setupTranslations({state}) {
                const r = await window.http.get(`${config.API_URL}/translations`);
                state.translations = {};

                r.data.forEach(t => {
                    state.translations[t.key] = t.value ? t.value : t.english_text;
                });

                app.config.globalProperties.$t = (key) => state.translations[key.trim().toLowerCase()] ?? key;
            },

            async discovery({commit}, filters = {}) {
                const r = await window.http.get(`${config.API_URL}/discovery`, filters);

                commit('setDiscoveryPeople', r.data);

                return r;
            },

            /* USER PROFILE */
            async updateUserProfile({dispatch, state}, data) {
                let r;

                if (state.isOperator) {
                    if (!state.chat.currentChatUser) {
                        return;
                    }

                    const userId = state.chat.currentChatUser.id;

                    r = await window.http.post(`${config.API_URL}/operator/users/${userId}/profile`, data);
                } else {
                    r = await window.http.post(`${config.API_URL}/profile`, data);
                }

                if (r.success) {
                    await dispatch('fetchCurrentChatUser', data);
                }

                return r;
            },

            async uploadImage({dispatch, state}, {category, picture}) {
                let formData = new FormData();
                formData.set('category', category);
                formData.set('picture', picture);
                formData.set('user_id', state.chat.currentChatUser.id);

                const r = await window.http.post(`${config.API_URL}/uploads`, formData, true);

                if (r.success) {
                    await dispatch('fetchCurrentChatUser');
                }

                return r;
            },

            async deleteFile({rootState, dispatch}, fileId) {
                const r = await window.http.delete(`${config.API_URL}/uploads/${fileId}`);

                if (r.success) {
                    await dispatch('fetchCurrentChatUser');
                }

                return r;
            },
        },
    });
};
